import React, { useState, useEffect } from "react";
import {
  getLicenses,
  getLicenseStats,
} from "../../services/api/admin/licenses";
import { AppLicense } from "../../types";
import {
  ShieldCheck,
  Calendar,
  Clock,
  CreditCard,
  AlertCircle,
  Info,
  Download,
  TrendingDown,
  Loader2,
} from "lucide-react";

export const LicenseManager: React.FC = () => {
  const [license, setLicense] = useState<AppLicense | null>(null);
  const [stats, setStats] = useState({
    totalFee: 0,
    freeTxCount: 0,
    billableTxCount: 0,
    totalTx: 0,
    serviceFeePercent: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [licenses, licenseStats] = await Promise.all([
          getLicenses(),
          getLicenseStats(),
        ]);

        if (licenses.length > 0) {
          setLicense(licenses[0]);
        }
        setStats(licenseStats);
      } catch (error) {
        console.error("Failed to fetch license data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  if (isLoading)
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-400">
        <Loader2 className="w-10 h-10 animate-spin mb-4" />
        <p className="font-bold font-mono uppercase tracking-[0.2em] text-xs">
          Fetching License Data...
        </p>
      </div>
    );

  if (!license)
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-400">
        <AlertCircle className="w-10 h-10 mb-4" />
        <p className="font-bold font-mono uppercase tracking-[0.2em] text-xs">
          No Active License Found
        </p>
      </div>
    );

  const startDate = new Date(license.startDate);
  const endDate = new Date(license.endDate);
  const freePeriodEnd = new Date(license.freePeriodEnd);

  const freeProgress = license.isInFreePeriod
    ? Math.min(
        100,
        Math.max(
          0,
          ((Date.now() - startDate.getTime()) /
            (freePeriodEnd.getTime() - startDate.getTime())) *
            100
        )
      )
    : 100;

  return (
    <div className="space-y-8 animate-fadeIn max-w-5xl mx-auto">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Lisensi & Tagihan
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Status berlangganan dan perhitungan biaya layanan bagi hasil
            (Revenue Share).
          </p>
        </div>
        <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg border border-green-100">
          <ShieldCheck className="w-5 h-5" />
          <span className="font-bold text-sm">Status: {license.status}</span>
        </div>
      </div>

      {/* Main License Card */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden relative">
        <div className="h-2 bg-gradient-to-r from-giz-primary to-indigo-600 w-full"></div>
        <div className="p-8">
          <div className="flex flex-col md:flex-row justify-between gap-8">
            {/* Info Kontrak */}
            <div className="flex-1 space-y-4">
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                Detail Kontrak
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                  <span className="text-gray-500 text-sm flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Mulai Kontrak
                  </span>
                  <span className="font-bold text-gray-900">
                    {startDate.toLocaleDateString("id-ID", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <div className="flex justify-between items-center border-b border-gray-100 pb-2">
                  <span className="text-gray-500 text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Berakhir
                  </span>
                  <span className="font-bold text-gray-900">
                    {endDate.toLocaleDateString("id-ID", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-1">
                  <span className="text-gray-500 text-sm">Masa Berlaku</span>
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-bold">
                    {license.remainingDays} Hari Lagi
                  </span>
                </div>
              </div>
            </div>

            {/* Status Free Trial */}
            <div className="flex-1 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
              <h3 className="text-sm font-bold text-indigo-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Status Biaya Layanan
              </h3>

              <div className="mb-4">
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-bold text-indigo-700">
                    Masa Gratis {license.freePeriodMonths} Bulan
                  </span>
                  <span className="text-indigo-500">
                    {Math.round(freeProgress)}% Terpakai
                  </span>
                </div>
                <div className="w-full bg-white h-3 rounded-full overflow-hidden border border-indigo-100">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ${
                      license.isInFreePeriod ? "bg-green-500" : "bg-gray-400"
                    }`}
                    style={{ width: `${freeProgress}%` }}
                  ></div>
                </div>
                <p className="text-[10px] text-indigo-400 mt-1">
                  {license.isInFreePeriod
                    ? `Bebas biaya layanan sampai ${freePeriodEnd.toLocaleDateString(
                        "id-ID"
                      )}`
                    : "Masa gratis telah berakhir. Biaya layanan normal berlaku."}
                </p>
              </div>

              <div className="flex gap-2">
                <div className="bg-white p-2 rounded-lg border border-indigo-100 flex-1 text-center">
                  <p className="text-[10px] text-gray-500 uppercase">
                    Tarif Bagi Hasil
                  </p>
                  <p className="font-bold text-indigo-700 flex items-center justify-center gap-1">
                    {(license.feePerTransaction * 100).toFixed(1)}%{" "}
                    <TrendingDown className="w-3 h-3 text-emerald-500" />
                  </p>
                </div>
                <div className="bg-white p-2 rounded-lg border border-indigo-100 flex-1 text-center">
                  <p className="text-[10px] text-gray-500 uppercase">
                    Status Saat Ini
                  </p>
                  <p
                    className={`font-bold ${
                      license.isInFreePeriod
                        ? "text-green-600"
                        : "text-indigo-600"
                    }`}
                  >
                    {license.isInFreePeriod ? "FREE" : "ACTIVE"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bill Calculation */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
          <h3 className="font-bold text-gray-900 mb-6">Ringkasan Transaksi</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 p-2 rounded-lg text-green-600 font-bold text-xs">
                  FREE
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">
                    Transaksi Masa Gratis
                  </p>
                  <p className="text-xs text-gray-500">0 - 3 Bulan Pertama</p>
                </div>
              </div>
              <span className="font-bold text-gray-900">
                {stats.freeTxCount}
              </span>
            </div>

            <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-xl border border-indigo-100">
              <div className="flex items-center gap-3">
                <div className="bg-indigo-100 p-2 rounded-lg text-indigo-600 font-bold text-xs">
                  BILL
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">
                    Transaksi Berbayar
                  </p>
                  <p className="text-xs text-gray-500">Setelah Masa Gratis</p>
                </div>
              </div>
              <span className="font-bold text-indigo-700">
                {stats.billableTxCount}
              </span>
            </div>

            <div className="pt-2 border-t border-gray-100 flex justify-between">
              <span className="text-sm text-gray-500">
                Total Semua Transaksi
              </span>
              <span className="text-sm font-bold">{stats.totalTx}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-900 text-white p-6 rounded-2xl shadow-lg relative overflow-hidden flex flex-col justify-between">
          <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
            <CreditCard className="w-40 h-40" />
          </div>

          <div className="relative z-10">
            <h3 className="font-bold text-lg mb-1">Estimasi Tagihan Layanan</h3>
            <p className="text-gray-400 text-xs mb-6">
              Akumulasi biaya layanan aplikator saat ini.
            </p>

            <div className="space-y-1 mb-6">
              <p className="text-4xl font-extrabold tracking-tight">
                Rp {stats.totalFee.toLocaleString("id-ID")}
              </p>
              <p className="text-sm text-gray-400">
                (Tarif {stats.serviceFeePercent.toFixed(1)}% per transaksi
                berbayar)
              </p>
            </div>
          </div>

          <div className="relative z-10 bg-white/10 p-4 rounded-xl backdrop-blur-sm border border-white/10">
            <div className="flex gap-2 items-start">
              <Info className="w-4 h-4 text-blue-300 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-gray-300 leading-relaxed">
                Tagihan dihitung otomatis berdasarkan total nilai transaksi
                (GMV) yang terjadi setelah masa gratis berakhir.
              </p>
            </div>
            <button className="w-full mt-3 bg-white text-gray-900 font-bold py-2 rounded-lg text-xs hover:bg-gray-100 flex items-center justify-center gap-2">
              <Download className="w-3 h-3" /> Download Rincian Tagihan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
